// "Save as image": the planning or the estimate drawn as a PNG, in the
// portal's own type and layout. It's drawn from the data (not a screenshot of
// the page), so it comes out the same whatever screen it's made on: 1600 wide
// at twice the resolution, page grey, the timeline in the section indicator's
// language, the estimate with its totals.
import { totals as sum, type Approval, type Project } from "../lib/auth"
import { day, fmtDate, fmtMoney } from "./api"
import { inLang, locale, t, unit } from "./i18n"

const W = 1600
const PAD = 96
const COL = 470 // content column, after the section label (as the page's grid)
const C = {
    page: "#e9e9e9",
    ink: "#171717",
    muted: "rgba(23,23,23,0.55)",
    rule: "rgba(23,23,23,0.3)",
    hair: "rgba(23,23,23,0.1)",
}
const F = {
    display: (px: number) => `700 ${px}px "Blubird Bold Display", sans-serif`,
    label: (px = 13) => `600 ${px}px "Blubird Semi Bold Display", sans-serif`,
    text: (px: number) => `500 ${px}px "Blubird Medium Text", sans-serif`,
    mono: (px = 13) => `500 ${px}px "NTO Mono", ui-monospace, monospace`,
}

type Opts = { project: Project; kind: "planning" | "estimate"; clientName: string; logo?: string | null; approval?: Approval | null; lang?: "en" | "nl" }

export const saveImage = (o: Opts) => inLang(o.lang, () => make(o))

async function make(o: Opts) {
    await Promise.all(
        [F.display(40), F.label(), F.text(16), F.mono()].map((f) => document.fonts?.load(f).catch(() => {}))
    )
    const logo = o.logo ? await loadImage(o.logo).catch(() => null) : null
    const nto = await loadImage("/nto-logo.svg").catch(() => null)
    // draw twice: once to measure the height, once for real
    const height = draw(null, o, logo, nto)
    const scale = 2
    const canvas = document.createElement("canvas")
    canvas.width = W * scale
    canvas.height = Math.ceil(height * scale)
    const g = canvas.getContext("2d")!
    g.scale(scale, scale)
    draw(g, o, logo, nto)
    const blob: Blob = await new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("no image"))), "image/png"))
    const name = `${(o.project.reference || o.project.id).replace(/[^\w-]+/g, "-")}-${o.kind}.png`
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = name
    document.body.append(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(a.href), 4000)
}

function loadImage(src: string) {
    return new Promise<HTMLImageElement>((res, rej) => {
        const i = new Image()
        i.onload = () => res(i)
        i.onerror = rej
        i.src = src
    })
}

/** draws (or, with g = null, only measures); returns the height */
function draw(g: CanvasRenderingContext2D | null, o: Opts, logo: HTMLImageElement | null, nto: HTMLImageElement | null): number {
    // a measuring context when only measuring
    const m = g || document.createElement("canvas").getContext("2d")!
    const on = !!g
    const text = (s: string, x: number, y: number, font: string, color = C.ink, align: CanvasTextAlign = "left") => {
        if (!on) return
        m.font = font
        m.fillStyle = color
        m.textAlign = align
        m.textBaseline = "alphabetic"
        m.fillText(s, x, y)
    }
    const width = (s: string, font: string) => ((m.font = font), m.measureText(s).width)
    const line = (x1: number, y1: number, x2: number, y2: number, color: string, w = 1) => {
        if (!on) return
        m.strokeStyle = color
        m.lineWidth = w
        m.beginPath()
        m.moveTo(x1, y1)
        m.lineTo(x2, y2)
        m.stroke()
    }
    const dot = (x: number, y: number, r: number, color: string) => {
        if (!on) return
        m.fillStyle = color
        m.beginPath()
        m.arc(x, y, r, 0, Math.PI * 2)
        m.fill()
    }
    /** wraps to a width; returns the lines */
    const wrap = (s: string, font: string, max: number) => {
        const words = s.split(/\s+/).filter(Boolean)
        const lines: string[] = []
        let cur = ""
        for (const w of words) {
            const next = cur ? cur + " " + w : w
            if (width(next, font) > max && cur) {
                lines.push(cur)
                cur = w
            } else cur = next
        }
        if (cur) lines.push(cur)
        return lines
    }

    const p = o.project
    if (on) {
        m.fillStyle = C.page
        m.fillRect(0, 0, W, 99999)
    }
    let y = PAD

    // top line: the office, and what this is
    // the NTO wordmark (154 × 15.9), then the signature
    const lw = 150, lh = (lw * 15.931) / 154
    if (nto && on) m.drawImage(nto, PAD, y - lh, lw, lh)
    else if (!nto) text("NTO", PAD, y, F.display(18))
    text("THE DESIGN OFFICE OF TIAGO NASCIMENTO", PAD + (nto ? lw + 20 : 54), y - 1, F.label(11))
    text(`${o.kind === "planning" ? t("Planning") : t("Estimate")} · ${fmtDate(new Date().toISOString().slice(0, 10), true)}`, W - PAD, y, F.mono(12), C.muted, "right")
    y += 90

    // client: logo square and name
    let x = PAD
    if (logo) {
        const s = 44
        if (on) {
            m.save()
            roundRect(m, x, y - s + 6, s, s, 10)
            m.clip()
            m.drawImage(logo, x, y - s + 6, s, s)
            m.restore()
        }
        x += s + 16
    }
    text(o.clientName, x, y, F.display(40))
    y += 44

    // the project: title, meta
    const titleFont = F.display(96)
    const titleLines = wrap(p.title, titleFont, W - PAD * 2)
    for (const l of titleLines) {
        y += 90
        text(l, PAD - 3, y, titleFont)
    }
    y += 44
    const meta = [p.client, p.reference, fmtDate(p.date, true)].filter(Boolean)
    let mx = PAD
    for (const s of meta) {
        text(s, mx, y, F.mono(14), C.muted)
        mx += width(s, F.mono(14)) + 32
    }
    y += 70

    line(PAD, y, W - PAD, y, C.rule)
    y += 40
    text((o.kind === "planning" ? t("Planning") : t("Estimate")).toUpperCase(), PAD, y + 4, F.label(13))

    const left = PAD + COL
    const right = W - PAD
    if (o.kind === "planning") y = drawPlanning(y)
    else y = drawEstimate(y)
    return y + PAD

    function drawPlanning(y: number) {
        const phases = p.planning.phases
        const nameW = 300
        const tl = left + nameW, tr = right
        const start = Math.min(...phases.map((ph) => day(ph.start)))
        const end = Math.max(...phases.map((ph) => day(ph.end) + 43200000))
        const at = (t: number) => tl + Math.min(1, Math.max(0, (t - start) / (end - start))) * (tr - tl)
        const now = Date.now()

        // months along the top
        text(fmtDate(phases[0].start).toUpperCase(), tl, y + 4, F.mono(11), C.muted)
        const mo = new Date(start)
        mo.setDate(1)
        mo.setMonth(mo.getMonth() + 1)
        while (mo.getTime() <= end) {
            const mx = at(mo.getTime())
            line(mx, y - 8, mx, y + 10, C.rule)
            text(new Intl.DateTimeFormat(locale(), { month: "short" }).format(mo).toUpperCase(), mx + 7, y + 4, F.mono(11), C.muted)
            mo.setMonth(mo.getMonth() + 1)
        }
        y += 34
        const top = y
        for (const ph of phases) {
            const noteLines = ph.note ? wrap(ph.note, F.text(15), nameW - 20) : []
            const h = Math.max(78, 36 + 22 + noteLines.length * 20 + 12)
            const s = day(ph.start), e = day(ph.end) + 43200000
            const done = Math.min(1, Math.max(0, (now - s) / (e - s)))
            text(ph.name, left, y + 32, F.text(20), done >= 1 ? C.muted : C.ink)
            text(`${fmtDate(ph.start)} – ${fmtDate(ph.end)}`, left, y + 54, F.mono(12), C.muted)
            noteLines.forEach((l, i) => text(l, left, y + 76 + i * 20, F.text(15), C.muted))
            const cy = y + 28
            const x1 = at(s), x2 = Math.max(at(e), x1 + 6)
            line(x1, cy, x2, cy, C.rule, 2)
            if (done > 0) line(x1, cy, x1 + (x2 - x1) * done, cy, C.ink, 2)
            dot(x1, cy, 2.6, C.ink)
            dot(x2, cy, 2.6, done >= 1 ? C.ink : C.rule)
            y += h
            line(left, y, right, y, C.hair)
        }
        if (now > start && now < end) {
            const tx = at(now)
            line(tx, top - 6, tx, y, C.ink)
            dot(tx, top - 6, 3, C.ink)
            text(t("Today").toUpperCase(), tx - 8, top - 14, F.mono(11), C.ink, "right")
        }
        const ms = p.planning.milestones || []
        if (ms.length) {
            y += 40
            let mx = left
            for (const mi of ms) {
                const s = `${mi.name} · ${fmtDate(mi.date)}`
                dot(mx + 3, y - 4, 3, C.ink)
                text(s, mx + 16, y, F.mono(13), C.muted)
                mx += width(s, F.mono(13)) + 56
            }
        }
        return y
    }

    function drawEstimate(y: number) {
        const e = p.estimate
        const cur = e.currency
        const tot = sum(e)
        const cQty = right - 420, cRate = right - 270, cAmt = right
        y -= 4
        for (const grp of e.groups) {
            text(grp.name.toUpperCase(), left, y + 4, F.label(12), C.muted)
            y += 20
            line(left, y, right, y, C.hair)
            for (const l of grp.lines) {
                const itemLines = wrap(l.item, F.text(19), cQty - left - 40)
                const noteLines = l.note ? wrap(l.note, F.text(15), cQty - left - 40) : []
                let ly = y + 34
                itemLines.forEach((s, i) => text(s, left, ly + i * 24, F.text(19)))
                const base = ly
                ly += itemLines.length * 24 - 24
                noteLines.forEach((s, i) => text(s, left, ly + 24 + i * 20, F.text(15), C.muted))
                text(l.unit === "fixed" ? unit("fixed") : `${new Intl.NumberFormat(locale()).format(l.qty)} ${unit(l.unit)}`, cQty, base, F.mono(13), C.muted)
                if (l.unit !== "fixed") text(`× ${fmtMoney(l.rate, cur, false)}`, cRate, base, F.mono(13), C.muted)
                text(fmtMoney(l.qty * l.rate, cur), cAmt, base, F.mono(13), C.ink, "right")
                y = ly + noteLines.length * 20 + 24
                line(left, y, right, y, C.hair)
            }
            y += 40
        }
        // sums
        const sx = right - 440
        text(t("Subtotal"), sx, y + 6, F.mono(13), C.muted)
        text(fmtMoney(tot.net, cur), right, y + 6, F.mono(13), C.muted, "right")
        y += 34
        text(`${t("VAT")} ${e.vat}%`, sx, y + 6, F.mono(13), C.muted)
        text(fmtMoney(tot.vat, cur), right, y + 6, F.mono(13), C.muted, "right")
        y += 26
        line(sx, y, right, y, C.ink)
        y += 64
        text(t("Total").toUpperCase(), sx, y - 4, F.label(13))
        text(fmtMoney(tot.total, cur), right, y, F.display(54), C.ink, "right")
        y += 70
        const notes = [
            o.approval ? `${t("Approved on")} ${fmtDate(o.approval.at.slice(0, 10), true)}` : e.validUntil ? `${t("Valid until")} ${fmtDate(e.validUntil, true)}` : "",
            ...(e.notes || []),
        ].filter(Boolean)
        for (const n of notes) {
            for (const l of wrap(n, F.mono(13), 640)) {
                text(l, left, y, F.mono(13), C.muted)
                y += 21
            }
            y += 6
        }
        return y
    }
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    g.beginPath()
    g.moveTo(x + r, y)
    g.arcTo(x + w, y, x + w, y + h, r)
    g.arcTo(x + w, y + h, x, y + h, r)
    g.arcTo(x, y + h, x, y, r)
    g.arcTo(x, y, x + w, y, r)
    g.closePath()
}
